/**
 * <load-more-products> wraps a "View More" button that fetches the next
 * paginated page and appends its product-grid items to the current grid,
 * instead of navigating to a numbered pagination page.
 *
 * data-grid-id / data-section-id together locate the <ul id="{grid-id}"
 * data-id="{section-id}"> to append into, both locally and in the fetched
 * page's HTML (a page can render more than one product grid section).
 * data-next-url holds the paginate.next.url for the currently loaded page
 * and is replaced with whatever the fetched page's own control reports;
 * once a fetched page has no further next link, this element removes
 * itself.
 */
(() => {
  if (customElements.get('load-more-products')) return;

  class LoadMoreProducts extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.spinner = this.querySelector('.loading__spinner');
      this.button.addEventListener('click', () => this.loadNextPage());
    }

    gridSelector() {
      return `#${this.dataset.gridId}[data-id="${this.dataset.sectionId}"]`;
    }

    async loadNextPage() {
      const nextUrl = this.dataset.nextUrl;
      if (!nextUrl || this.button.disabled) return;

      this.button.disabled = true;
      this.button.classList.add('loading');
      this.spinner.classList.remove('hidden');

      try {
        const response = await fetch(nextUrl);
        if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

        const currentGrid = document.querySelector(this.gridSelector());
        const fetchedGrid = doc.querySelector(this.gridSelector());
        if (!currentGrid || !fetchedGrid) {
          this.remove();
          return;
        }

        Array.from(fetchedGrid.children).forEach((item) => currentGrid.appendChild(item));

        const fetchedControl = doc.querySelector('load-more-products');
        if (fetchedControl && fetchedControl.dataset.nextUrl) {
          this.dataset.nextUrl = fetchedControl.dataset.nextUrl;
        } else {
          this.remove();
          return;
        }
      } catch (error) {
        console.error('Failed to load more products', error);
      } finally {
        if (this.isConnected) {
          this.button.disabled = false;
          this.button.classList.remove('loading');
          this.spinner.classList.add('hidden');
        }
      }
    }
  }

  customElements.define('load-more-products', LoadMoreProducts);
})();
